/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect, useRef } from "react";
import { Box, Icon } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import colors from "assets/theme/base/colors";
import Scrollbars from "react-custom-scrollbars";
import useChatSupport from "hooks/useChatSupport";
import useModal from "hooks/useModal";
import ConfirmModal from "components/modal/ConfirmModal";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import dayjs from "dayjs";

const ChatSupport = () => {
  const [isMinimized, setIsMinimized] = useState(false);
  const axios = useAxiosPrivate();
  const [agentConnected, setAgentConnected] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef();
  const inputRef = useRef();
  const { setOpen } = useChatSupport();
  const [dots, setDots] = useState(1);
  const { openModal } = useModal();

  useEffect(() => {
    let agentTimeout;
    let messageTimeout;
    if (!agentConnected) {
      agentTimeout = setInterval(async () => {
        try {
          const response = await axios.get(`/chat/agent/connect`, {
            headers: { "Content-Type": "application/json" },
          });
          if (response.status === 200) {
            setAgentConnected(true);
            clearInterval(agentTimeout); // Stop checking once connected
          }
        } catch (error) {
          // fail silently
        }
      }, 1000);
    }

    if (agentConnected) {
      messageTimeout = setInterval(async () => {
        try {
          const response = await axios.get(`/chat/messages`, {
            headers: { "Content-Type": "application/json" },
          });
          if (response.status === 200) {
            if (response.data != null && response.data.length > 0) {
              setMessages((prevMessages) => [
                ...prevMessages,
                ...response.data.filter((msg) => !msg.typing),
              ]);

              if (isMinimized) {
                setUnreadMessages(
                  (prevUnread) => prevUnread + response.data.filter((msg) => !msg.typing).length
                );
              }

              if (response.data[response.data.length - 1].typing) {
                setIsTyping(true);
              } else {
                setIsTyping(false);
              }
            } else {
              setIsTyping(false);
            }
          }
        } catch (error) {
          // fail silently
        }
      }, 1000);
    }

    return () => {
      clearInterval(agentTimeout);
      clearInterval(messageTimeout);
    };
  }, [agentConnected]);

  useEffect(() => {
    if (agentConnected) {
      inputRef?.current?.focus();
    }
  }, [agentConnected, isMinimized]);

  useEffect(() => {
    scrollRef?.current?.scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev === 3 ? 1 : prev + 1));
    }, 200);

    return () => clearInterval(interval);
  }, []);

  const handleSend = async () => {
    if (newMessage.trim()) {
      const timestamp = dayjs().unix();
      setMessages([...messages, { sender: "user", text: newMessage, timestamp }]);
      setNewMessage("");
      setUnreadMessages(unreadMessages + 1);
      try {
        const response = await axios.post(
          `/chat/messages`,
          { text: newMessage, timestamp },
          {
            headers: { "Content-Type": "application/json" },
          }
        );
        if (response.status === 200) {
          setAgentConnected(true);
        }
      } catch (error) {
        // fail silently
      }
    }
  };

  const handleMinimize = () => {
    setIsMinimized(true);
    setUnreadMessages(0);
  };

  const handleMaximize = () => {
    setIsMinimized(false);
    setUnreadMessages(0);
  };

  const closeSession = () => {
    setMessages([]);
    setIsMinimized(false);
    setUnreadMessages(0);
    setAgentConnected(false);
    setOpen(false);
  };

  const handleClose = () => {
    openModal(
      <ConfirmModal
        buttonLabels={["no", "yes"]}
        icon="warn"
        text="Do you want to close this chat support session?"
      />,
      closeSession
    );
  };

  return (
    <MDBox
      style={{
        position: "fixed",
        bottom: 16,
        right: 16,
        width: isMinimized ? 80 : 300,
        height: isMinimized ? 42 : 400,
        backgroundColor: "white",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
        borderRadius: 12,
        display: "flex",
        flexDirection: "column",
        transition: "all 0.3s ease-in-out",
        opacity: 1,
      }}
    >
      {isMinimized ? (
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          bgColor="white"
          p={0.3}
          borderRadius={12}
        >
          <MDBadge
            badgeContent={unreadMessages}
            color="error"
            size="xs"
            circular
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            overlap="circular"
          >
            <MDButton variant="text" color="secondary" iconOnly circular onClick={handleMaximize}>
              <Icon>chat</Icon>
            </MDButton>
          </MDBadge>

          <MDButton variant="text" color="secondary" iconOnly circular onClick={handleClose}>
            <Icon>close</Icon>
          </MDButton>
        </MDBox>
      ) : (
        <>
          {/* Header */}
          <MDBox
            bgColor="info"
            variant="gradient"
            p={1}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ borderRadius: "12px 12px 0px 0px" }}
          >
            <MDTypography variant="body2" fontWeight="regular" color="white" sx={{ marginLeft: 1 }}>
              Chat Support
            </MDTypography>
            <Box>
              <MDButton variant="text" color="white" iconOnly circular onClick={handleMinimize}>
                <Icon>minimize</Icon>
              </MDButton>
              <MDButton variant="text" color="white" iconOnly circular onClick={handleClose}>
                <Icon>close</Icon>
              </MDButton>
            </Box>
          </MDBox>

          {/* Chat Area */}
          <Scrollbars
            ref={scrollRef}
            style={{
              height: "100%",
            }}
            renderTrackHorizontal={({ style, ...props }) => <div />}
            renderTrackVertical={({ style, ...props }) => (
              <div className="scrollbar-regular-vertical-track" {...props} style={{ right: 0 }} />
            )}
            renderThumbVertical={({ style, ...props }) => (
              <div
                style={{
                  ...style,
                  backgroundColor: "rgba(0, 0, 0, 0.2)",
                  borderRadius: 8,
                  width: 6,
                  cursor: "pointer",
                }}
                {...props}
              />
            )}
          >
            <Box
              sx={{
                flex: 1,
                padding: "8px",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#ffffff",
                height: "100%",
              }}
            >
              {!agentConnected ? (
                <MDBox display="flex" justifyContent="center" alignItems="center" height="100%">
                  <MDTypography
                    variant="body2"
                    fontWeight="regular"
                    color="text"
                    style={{ fontSize: 12, width: 180 }}
                  >
                    Connecting you with an agent{".".repeat(dots)}
                  </MDTypography>
                </MDBox>
              ) : (
                <>
                  {/* New Element */}
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    sx={{
                      padding: 1,
                      backgroundColor: colors.light.main,
                      borderRadius: "8px",
                      marginBottom: 2,
                    }}
                  >
                    <MDTypography variant="body2" color="dark" style={{ fontSize: 12 }}>
                      You are now connected with an agent.
                    </MDTypography>
                  </MDBox>

                  {/* Existing Message List */}
                  {messages.map((msg, index) => (
                    <React.Fragment key={index}>
                      {msg.sender === "user" && (
                        <>
                          <MDBox
                            sx={{
                              alignSelf: "flex-end",
                            }}
                          >
                            <MDBox
                              px={0.8}
                              py={0.4}
                              mb={0.8}
                              sx={{
                                backgroundColor: colors.dark.main,
                                borderRadius: "8px",
                              }}
                            >
                              <MDTypography
                                variant="body2"
                                fontWeight="regular"
                                color="white"
                                style={{ fontSize: 14 }}
                                textAlign="left"
                              >
                                {msg.text}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                          <MDBox
                            sx={{
                              alignSelf: "flex-end",
                            }}
                          >
                            {(messages[index + 1] == null ||
                              messages[index + 1]?.sender !== "user") && (
                              <MDTypography
                                variant="body2"
                                fontWeight="regular"
                                color="text"
                                style={{ fontSize: 10, marginRight: 4, marginTop: -6 }}
                              >
                                You &bull; {dayjs(msg.timestamp).format("hh:mm A")}
                              </MDTypography>
                            )}
                          </MDBox>
                        </>
                      )}
                      {msg.sender !== "user" && (
                        <MDBox textAlign="left">
                          <MDBox alignItems="center" display="flex">
                            <MDBox
                              px={0.8}
                              py={0.4}
                              mb={0.8}
                              sx={{
                                backgroundColor: "#e3f2fd",
                                borderRadius: "8px",
                              }}
                            >
                              <MDTypography
                                variant="body2"
                                fontWeight="regular"
                                color="dark"
                                style={{ fontSize: 14 }}
                                textAlign="left"
                              >
                                {msg.text}
                              </MDTypography>
                            </MDBox>
                          </MDBox>

                          {(messages[index + 1] == null ||
                            messages[index + 1]?.sender === "user") && (
                            <MDTypography
                              variant="body2"
                              fontWeight="regular"
                              color="text"
                              style={{ fontSize: 10, marginRight: 4, marginTop: -6 }}
                            >
                              Agent &bull; {dayjs(msg.timestamp).format("hh:mm A")}
                            </MDTypography>
                          )}
                        </MDBox>
                      )}
                    </React.Fragment>
                  ))}
                </>
              )}
              {/* Typing Indicator */}
              {isTyping && (
                <MDBox mb={1}>
                  <MDBox
                    px={0.8}
                    py={0.4}
                    sx={{
                      alignSelf: "flex-start",
                      backgroundColor: "#EEF7FDFF",
                      borderRadius: "8px",
                      width: 116,
                    }}
                  >
                    <MDTypography
                      variant="body2"
                      fontWeight="regular"
                      color="text"
                      style={{ fontSize: 13, fontStyle: "italic" }}
                    >
                      Agent is typing{".".repeat(dots)}
                    </MDTypography>
                  </MDBox>
                  <div style={{ height: 12 }} />
                </MDBox>
              )}
            </Box>
          </Scrollbars>

          {/* Input Area */}

          <MDBox
            display="flex"
            alignItems="center"
            p={1}
            sx={{
              borderTop: "1px solid #f0f0f0",
            }}
          >
            <MDInput
              value={newMessage}
              size="small"
              fullWidth
              autoComplete="off"
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSend();
                }
              }}
              placeholder={
                agentConnected ? "Type a message" : "Type a message once an agent connects"
              }
              disabled={!agentConnected}
              inputRef={inputRef}
            />

            <MDButton
              disabled={!agentConnected}
              variant="gradient"
              color="info"
              iconOnly
              onClick={handleSend}
              style={{ marginLeft: 4 }}
            >
              <Icon>send</Icon>
            </MDButton>
          </MDBox>
        </>
      )}
    </MDBox>
  );
};

export default ChatSupport;
